import React, { useState, useEffect, useMemo } from 'react';
import { useHOA } from '../../contexts/HOAContext';
import { hoaProfile } from '../../lib/hoaProfile';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import StatusPill from '../ui/StatusPill';

/**
 * CommunityDirectory — the member-facing directory.
 *
 * Privacy rules (non-negotiable):
 *   * shows ONLY legal owner name + situs address
 *   * no email, phone, member name, or mailing address
 *
 * Owners with multiple properties (same owner_name_legal) are consolidated
 * into a single entry listing all their addresses.
 */
const CommunityDirectory = () => {
  const { hoa, isLoading: hoaLoading } = useHOA();
  const { effectiveRole } = useAuth();
  const showActivationPill = effectiveRole === 'management';
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchProperties = async () => {
      if (hoaLoading) {
        setLoading(true);
        return;
      }

      if (!hoa?.id) {
        setLoading(false);
        setError('Community context is not ready. Please refresh or sign in again.');
        return;
      }

      setLoading(true);
      setError('');
      try {
        // directory_properties is a column-restricted view (legal owner name +
        // situs address only). Members have NO direct access to the properties
        // base table — private contact info never leaves the database.
        const { data, error } = await supabase
          .from('directory_properties')
          .select('id, situs_address, owner_name_legal, block, lot')
          .eq('hoa_id', hoa.id)
          .order('block')
          .order('lot');
        if (error) throw error;

        // Management-only: fetch invitation status per property so we can flag
        // owners whose portal accounts are still pending activation.
        let activatedPropertyIds = new Set();
        let invitationsAvailable = true;
        if (showActivationPill) {
          try {
            const { data: invitations } = await supabase
              .from('member_invitations')
              .select('property_id, status')
              .eq('hoa_id', hoa.id)
              .not('property_id', 'is', null);
            (invitations || []).forEach((inv) => {
              if (inv.status === 'activated' && inv.property_id) {
                activatedPropertyIds.add(inv.property_id);
              }
            });
          } catch (invErr) {
            // Silent fallback: pill just won't render if RLS blocks or query fails.
            console.warn('CommunityDirectory: invitation status unavailable', invErr);
            invitationsAvailable = false;
          }
        }

        const grouped = {};
        (data || []).forEach((p) => {
          const name = (p.owner_name_legal || '').trim() || 'Owner of record';
          const key = name.toUpperCase();
          if (!grouped[key]) grouped[key] = { name, properties: [], propertyIds: [] };
          if (p.situs_address) {
            grouped[key].properties.push({
              address: p.situs_address,
              cadastral: `Block ${p.block || '-'} / Lot ${p.lot || '-'}`,
              propertyId: p.id,
            });
            if (p.id) grouped[key].propertyIds.push(p.id);
          }
        });

        const withStatus = Object.values(grouped).map((owner) => {
          if (!showActivationPill || !invitationsAvailable) {
            return { ...owner, activationStatus: null };
          }
          const ids = owner.propertyIds;
          if (!ids.length) return { ...owner, activationStatus: null };
          const allActivated = ids.every((id) => activatedPropertyIds.has(id));
          return { ...owner, activationStatus: allActivated ? 'activated' : 'pending' };
        });

        setOwners(withStatus.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) {
        console.error('CommunityDirectory: fetch error', e);
        setOwners([]);
        setError(e?.message || 'Directory could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [hoaLoading, hoa?.id, showActivationPill]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return owners;
    return owners.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.properties.some((p) => `${p.address} ${p.cadastral}`.toLowerCase().includes(q))
    );
  }, [owners, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold tracking-tight text-primary-dark">Community Directory</h2>
        <p className="text-sm font-medium text-slate-500 mt-1">
          Member information is a reflection of public data from Montana cadastral records.
        </p>
      </div>

      <section className="bg-section-bg p-4 sm:p-6 lg:p-8 rounded-[2.5rem] space-y-6">
        {error && (
          <div className="bg-white rounded-2xl p-4 border border-red-100 text-red-700 text-sm font-bold">
            {error}
          </div>
        )}

        <div className="sticky top-[max(5rem,env(safe-area-inset-top))] z-10 md:static relative">
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" style={{ fontSize: '20px' }}>search</span>
          <input
            type="text"
            placeholder="Search by owner, address, block, or lot..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-12 py-3 bg-white border border-amber-100/30 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-full text-primary-dark placeholder-slate-400 text-base md:text-sm font-medium outline-none transition-all warm-tactile-shadow"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full hover:bg-section-bg text-slate-400 hover:text-primary-dark flex items-center justify-center"
              aria-label="Clear directory search"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl warm-tactile-shadow">
            <div className="w-16 h-16 bg-section-bg rounded-2xl flex items-center justify-center mb-6 warm-tactile-shadow">
              <span className="material-symbols-outlined text-3xl text-secondary">person_search</span>
            </div>
            <h3 className="text-xl font-bold text-primary-dark mb-2">No owners found</h3>
            <p className="text-sm font-medium text-slate-500 max-w-sm leading-relaxed">
              Try a different name or address. The directory lists every {hoaProfile(hoa).shortName} property owner.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 overflow-hidden" data-testid="directory-list">
            <div className="block">
              <table className="w-full table-fixed">
                <colgroup><col className="w-[42%]" /><col className="w-[58%]" /></colgroup>
                <thead className="bg-section-bg">
                  <tr>
                    <th className="text-left px-4 sm:px-6 py-4 text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest">Legal Owner</th>
                    <th className="text-left px-4 sm:px-6 py-4 text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest">Properties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100/30">
                  {filtered.map((owner) => (
                    <tr key={owner.name} data-testid="directory-entry" className="hover:bg-section-bg/50 transition-colors">
                      <td className="px-4 sm:px-6 py-5 align-top">
                        <div className="flex items-start gap-2 flex-wrap">
                          <p className="font-bold text-primary-dark text-xs sm:text-sm leading-tight break-words">{owner.name}</p>
                          {showActivationPill && owner.activationStatus && (
                            <StatusPill
                              label={owner.activationStatus === 'activated' ? 'Activated' : 'Pending'}
                              variant={owner.activationStatus === 'activated' ? 'active' : 'pending'}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-5">
                        <div className="space-y-3">
                          {owner.properties.map((property) => (
                            <div key={`${owner.name}-${property.address}`} className="leading-tight">
                              <p className="text-xs sm:text-sm font-bold text-primary-dark break-words">{property.address}</p>
                              <p className="text-[11px] sm:text-xs font-medium text-slate-500 mt-1">{property.cadastral}</p>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="hidden">
              {filtered.map((owner) => (
                <article key={owner.name} data-testid="directory-entry" className="p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-section-bg flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-secondary" style={{ fontSize: '22px' }}>person</span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-primary-dark text-base leading-snug break-words">{owner.name}</p>
                        {showActivationPill && owner.activationStatus && (
                          <StatusPill
                            label={owner.activationStatus === 'activated' ? 'Activated' : 'Pending'}
                            variant={owner.activationStatus === 'activated' ? 'active' : 'pending'}
                          />
                        )}
                      </div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mt-1">
                        {owner.properties.length} propert{owner.properties.length === 1 ? 'y' : 'ies'}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {owner.properties.map((property) => (
                      <div key={`${owner.name}-${property.address}`} className="rounded-2xl bg-section-bg border border-amber-100/40 p-4">
                        <p className="text-sm font-bold text-primary-dark leading-snug">{property.address}</p>
                        <p className="text-[11px] sm:text-xs font-medium text-slate-500 mt-1">{property.cadastral}</p>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default CommunityDirectory;
